const DRIVE_API_URL = 'https://www.googleapis.com/drive/v3'
const DRIVE_UPLOAD_URL = 'https://www.googleapis.com/upload/drive/v3'
const FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder'
const folderCache = new Map<string, string>()

export function clearGoogleDriveFolderCache() {
  folderCache.clear()
}

export class GoogleDriveRequestError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message)
  }
}

interface DriveFile {
  id: string
  name: string
  mimeType?: string
}

interface UpsertDriveFileInput {
  accessToken: string
  folderSegments: string[]
  name: string
  content: Blob
  existingUrl?: string | null
  relocateExisting?: boolean
}

function escapeQueryValue(value: string) {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")
}

async function driveFetch(accessToken: string, input: RequestInfo | URL, init: RequestInit = {}) {
  const response = await fetch(input, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...init.headers,
    },
  })
  if (!response.ok) {
    throw new GoogleDriveRequestError(`Google Drive request failed (${response.status}).`, response.status)
  }
  return response
}

async function findChild(accessToken: string, name: string, parentId?: string, mimeType?: string): Promise<DriveFile | null> {
  const clauses = [`name='${escapeQueryValue(name)}'`, 'trashed=false']
  if (parentId) clauses.push(`'${escapeQueryValue(parentId)}' in parents`)
  if (mimeType) clauses.push(`mimeType='${escapeQueryValue(mimeType)}'`)
  const params = new URLSearchParams({
    q: clauses.join(' and '),
    fields: 'files(id,name,mimeType)',
    pageSize: '10',
  })
  const response = await driveFetch(accessToken, `${DRIVE_API_URL}/files?${params}`)
  const data = await response.json() as { files?: DriveFile[] }
  return data.files?.[0] ?? null
}

async function createFolder(accessToken: string, name: string, parentId?: string) {
  const response = await driveFetch(accessToken, `${DRIVE_API_URL}/files?fields=id,name,mimeType`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      mimeType: FOLDER_MIME_TYPE,
      ...(parentId ? { parents: [parentId] } : {}),
      appProperties: { managedBy: 'english.hocnhe.com' },
    }),
  })
  return response.json() as Promise<DriveFile>
}

export async function ensureGoogleDriveFolderPath(accessToken: string, segments: string[]) {
  let parentId: string | undefined
  const traversed: string[] = []
  for (const rawSegment of segments) {
    const segment = rawSegment.trim()
    if (!segment) throw new Error('Google Drive folder segment không hợp lệ.')
    traversed.push(segment)
    const cacheKey = traversed.join('/')
    const cached = folderCache.get(cacheKey)
    if (cached) {
      parentId = cached
      continue
    }
    const existing = await findChild(accessToken, segment, parentId, FOLDER_MIME_TYPE)
    const folder = existing ?? await createFolder(accessToken, segment, parentId)
    folderCache.set(cacheKey, folder.id)
    parentId = folder.id
  }
  if (!parentId) throw new Error('Google Drive folder path không hợp lệ.')
  return parentId
}

function multipartBody(metadata: Record<string, unknown>, content: Blob) {
  const boundary = `hocnhe-${crypto.randomUUID()}`
  const delimiter = `\r\n--${boundary}\r\n`
  const end = `\r\n--${boundary}--`
  return {
    body: new Blob([
      delimiter,
      'Content-Type: application/json; charset=UTF-8\r\n\r\n',
      JSON.stringify(metadata),
      delimiter,
      `Content-Type: ${content.type || 'application/octet-stream'}\r\n\r\n`,
      content,
      end,
    ], { type: `multipart/related; boundary=${boundary}` }),
    boundary,
  }
}

async function createDriveFile(accessToken: string, folderId: string, name: string, content: Blob) {
  const multipart = multipartBody({ name, mimeType: content.type, parents: [folderId], appProperties: { managedBy: 'english.hocnhe.com' } }, content)
  const response = await driveFetch(accessToken, `${DRIVE_UPLOAD_URL}/files?uploadType=multipart&fields=id,name,mimeType`, {
    method: 'POST',
    headers: { 'Content-Type': `multipart/related; boundary=${multipart.boundary}` },
    body: multipart.body,
  })
  return response.json() as Promise<DriveFile>
}

async function updateDriveFile(
  accessToken: string,
  fileId: string,
  name: string,
  content: Blob,
  parentChange?: { add?: string; remove?: string[] },
) {
  const multipart = multipartBody({ name, mimeType: content.type }, content)
  const params = new URLSearchParams({ uploadType: 'multipart', fields: 'id,name,mimeType' })
  if (parentChange?.add) params.set('addParents', parentChange.add)
  if (parentChange?.remove?.length) params.set('removeParents', parentChange.remove.join(','))
  const response = await driveFetch(accessToken, `${DRIVE_UPLOAD_URL}/files/${encodeURIComponent(fileId)}?${params}`, {
    method: 'PATCH',
    headers: { 'Content-Type': `multipart/related; boundary=${multipart.boundary}` },
    body: multipart.body,
  })
  return response.json() as Promise<DriveFile>
}

async function driveFileParents(accessToken: string, fileId: string) {
  const response = await driveFetch(accessToken, `${DRIVE_API_URL}/files/${encodeURIComponent(fileId)}?fields=parents`)
  const data = await response.json() as { parents?: string[] }
  return data.parents ?? []
}

async function ensurePublicReader(accessToken: string, fileId: string) {
  const response = await driveFetch(accessToken, `${DRIVE_API_URL}/files/${encodeURIComponent(fileId)}/permissions?fields=permissions(id,type,role,allowFileDiscovery)`)
  const data = await response.json() as { permissions?: Array<{ id: string; type: string; role: string; allowFileDiscovery?: boolean }> }
  const publicPermission = data.permissions?.find((permission) => permission.type === 'anyone' && permission.role === 'reader')
  if (publicPermission) {
    if (publicPermission.allowFileDiscovery !== false) {
      await driveFetch(accessToken, `${DRIVE_API_URL}/files/${encodeURIComponent(fileId)}/permissions/${encodeURIComponent(publicPermission.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowFileDiscovery: false }),
      })
    }
    return
  }
  await driveFetch(accessToken, `${DRIVE_API_URL}/files/${encodeURIComponent(fileId)}/permissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'anyone', role: 'reader', allowFileDiscovery: false }),
  })
}

async function createPublicReader(accessToken: string, fileId: string) {
  await driveFetch(accessToken, `${DRIVE_API_URL}/files/${encodeURIComponent(fileId)}/permissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'anyone', role: 'reader', allowFileDiscovery: false }),
  })
}

export function extractGoogleDriveFileId(url: string | null | undefined) {
  if (!url) return null
  try {
    const parsed = new URL(url)
    const queryId = parsed.searchParams.get('id')
    if (queryId) return queryId
    const pathMatch = parsed.pathname.match(/\/files\/([^/]+)/) ?? parsed.pathname.match(/\/d\/([^/]+)/)
    return pathMatch?.[1] ?? null
  } catch {
    return null
  }
}

export function publicGoogleDriveUrl(fileId: string) {
  return `https://drive.google.com/uc?export=download&id=${encodeURIComponent(fileId)}`
}

export async function upsertPublicGoogleDriveFile(input: UpsertDriveFileInput) {
  const existingId = extractGoogleDriveFileId(input.existingUrl)
  if (existingId) {
    try {
      if (input.relocateExisting) {
        const folderId = await ensureGoogleDriveFolderPath(input.accessToken, input.folderSegments)
        const currentParents = await driveFileParents(input.accessToken, existingId)
        const file = await updateDriveFile(input.accessToken, existingId, input.name, input.content, {
          ...(currentParents.includes(folderId) ? {} : { add: folderId }),
          remove: currentParents.filter((parentId) => parentId !== folderId),
        })
        return { id: file.id, url: publicGoogleDriveUrl(file.id) }
      }
      const file = await updateDriveFile(input.accessToken, existingId, input.name, input.content)
      return { id: file.id, url: publicGoogleDriveUrl(file.id) }
    } catch (error) {
      if (!(error instanceof GoogleDriveRequestError) || error.status !== 404) throw error
    }
  }

  const folderId = await ensureGoogleDriveFolderPath(input.accessToken, input.folderSegments)
  const existing = await findChild(input.accessToken, input.name, folderId)
  let file: DriveFile
  if (existing) {
    file = await updateDriveFile(input.accessToken, existing.id, input.name, input.content)
    await ensurePublicReader(input.accessToken, file.id)
  } else {
    file = await createDriveFile(input.accessToken, folderId, input.name, input.content)
    await createPublicReader(input.accessToken, file.id)
  }
  return { id: file.id, url: publicGoogleDriveUrl(file.id) }
}

export async function downloadGoogleDriveBlob(url: string, accessToken?: string | null): Promise<Blob> {
  const fileId = extractGoogleDriveFileId(url)
  const response = fileId && accessToken
    ? await driveFetch(accessToken, `${DRIVE_API_URL}/files/${encodeURIComponent(fileId)}?alt=media`)
    : await fetch(url)
  if (!response.ok) throw new GoogleDriveRequestError(`Không thể tải file Google Drive (${response.status}).`, response.status)
  return response.blob()
}

export async function downloadGoogleDriveJson<T>(url: string, accessToken?: string | null): Promise<T> {
  const fileId = extractGoogleDriveFileId(url)
  const response = fileId && accessToken
    ? await driveFetch(accessToken, `${DRIVE_API_URL}/files/${encodeURIComponent(fileId)}?alt=media`)
    : await fetch(url)
  if (!response.ok) throw new GoogleDriveRequestError(`Không thể tải dữ liệu Google Drive (${response.status}).`, response.status)
  return response.json() as Promise<T>
}
